import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Loader2, Plus, Trash2 } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import api from '@/services/api';
import { PriceType } from '@/hooks/usePriceMetadata';
import { useSettingspageTranslations } from '@/hooks/useSettingspageTranslations';

interface ManagePriceTypesModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: (priceType: PriceType) => void;
}

export function ManagePriceTypesModal({ open, onClose, onSuccess }: ManagePriceTypesModalProps) {
  const [isCreating, setIsCreating] = useState(false);
  const queryClient = useQueryClient();
  const i18n = useSettingspageTranslations();

  const priceTypeSchema = z.object({
    code: z.string().min(1, 'Code is required').max(50, 'Code must be less than 50 characters'),
    label: z
      .string()
      .min(1, 'Label is required')
      .max(100, 'Label must be less than 100 characters'),
  });

  type PriceTypeFormValues = z.infer<typeof priceTypeSchema>;

  const form = useForm<PriceTypeFormValues>({
    resolver: zodResolver(priceTypeSchema),
    defaultValues: {
      code: '',
      label: '',
    },
  });

  // Fetch organization price types
  const {
    data: priceTypes = [],
    isLoading,
    refetch,
  } = useQuery<PriceType[]>({
    queryKey: ['priceTypes'],
    queryFn: async () => {
      const response = await api.get('/api/price-types/');
      return response.data;
    },
    enabled: open,
  });

  // Create price type mutation
  const { mutate: createPriceType, isPending: isCreatingPriceType } = useMutation({
    mutationFn: async (data: PriceTypeFormValues) => {
      const response = await api.post('/api/price-types/', data);
      return response.data;
    },
    onSuccess: newPriceType => {
      toast.success('Price type created successfully');
      form.reset();
      setIsCreating(false);
      refetch();
      // Invalidate all related queries
      queryClient.invalidateQueries({ queryKey: ['priceTypes'] });
      queryClient.invalidateQueries({ queryKey: ['currencies'] });
      queryClient.invalidateQueries({ queryKey: ['organization'] });
      queryClient.invalidateQueries({ queryKey: ['locales'] });
      queryClient.invalidateQueries({ queryKey: ['channels'] });
      // Also invalidate product queries since prices might be affected
      queryClient.invalidateQueries({ queryKey: ['products'], exact: false });
      queryClient.invalidateQueries({ queryKey: ['prices'] });
      onSuccess?.(newPriceType);
    },
    onError: (error: any) => {
      const message =
        error?.response?.data?.detail ||
        error?.response?.data?.code?.[0] ||
        'Failed to create price type';
      toast.error(message);
    },
  });

  // Delete price type mutation
  const { mutate: deletePriceType } = useMutation({
    mutationFn: async (priceTypeId: number) => {
      await api.delete(`/api/price-types/${priceTypeId}/`);
    },
    onSuccess: () => {
      toast.success('Price type deleted successfully');
      refetch();
      // Invalidate all related queries
      queryClient.invalidateQueries({ queryKey: ['priceTypes'] });
      queryClient.invalidateQueries({ queryKey: ['currencies'] });
      queryClient.invalidateQueries({ queryKey: ['organization'] });
      queryClient.invalidateQueries({ queryKey: ['locales'] });
      queryClient.invalidateQueries({ queryKey: ['channels'] });
      // Also invalidate product queries since prices might be affected
      queryClient.invalidateQueries({ queryKey: ['products'], exact: false });
      queryClient.invalidateQueries({ queryKey: ['prices'] });
      // Trigger parent component refetch
      onSuccess?.(null as any); // This will trigger useOrgSettings refetch
    },
    onError: (error: any) => {
      const message = error?.response?.data?.detail || 'Failed to delete price type';
      toast.error(message);
    },
  });

  const handleSubmit = (data: PriceTypeFormValues) => {
    createPriceType(data);
  };

  const handleDelete = (priceType: PriceType) => {
    const confirmMessage = `Are you sure you want to delete the price type "${priceType.label}" (${priceType.code})? This action cannot be undone.`;

    if (confirm(confirmMessage)) {
      deletePriceType(priceType.id);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-h-[80vh] max-w-4xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{i18n.organization.managePriceTypes.title}</DialogTitle>
          <DialogDescription>{i18n.organization.managePriceTypes.description}</DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* Create Price Type Section */}
          <div className="rounded-lg border p-4">
            <div className="mb-4 flex items-center justify-between">
              <h3 className="text-lg font-medium">
                {i18n.organization.managePriceTypes.addNewTitle}
              </h3>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setIsCreating(!isCreating)}
              >
                <Plus className="mr-2 size-4" />
                {isCreating
                  ? i18n.organization.managePriceTypes.cancelButton
                  : i18n.organization.managePriceTypes.addPriceTypeButton}
              </Button>
            </div>

            {isCreating && (
              <Form {...form}>
                <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <FormField
                      control={form.control}
                      name="code"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>{i18n.organization.managePriceTypes.codeLabel}</FormLabel>
                          <FormControl>
                            <Input
                              {...field}
                              placeholder={i18n.organization.managePriceTypes.codePlaceholder}
                              style={{ textTransform: 'lowercase' }}
                              onChange={e =>
                                field.onChange(
                                  e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '')
                                )
                              }
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="label"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>{i18n.organization.managePriceTypes.labelLabel}</FormLabel>
                          <FormControl>
                            <Input
                              {...field}
                              placeholder={i18n.organization.managePriceTypes.labelPlaceholder}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                  <div className="flex gap-2">
                    <Button type="submit" disabled={isCreatingPriceType}>
                      {isCreatingPriceType && <Loader2 className="mr-2 size-4 animate-spin" />}
                      {i18n.organization.managePriceTypes.createButton}
                    </Button>
                    <Button type="button" variant="outline" onClick={() => setIsCreating(false)}>
                      {i18n.organization.managePriceTypes.cancelButton}
                    </Button>
                  </div>
                </form>
              </Form>
            )}
          </div>

          {/* Price Types List */}
          <div className="rounded-lg border">
            <div className="border-b p-4">
              <h3 className="text-lg font-medium">
                {i18n.organization.managePriceTypes.organizationPriceTypesTitle}
              </h3>
              <p className="text-sm text-muted-foreground">
                {i18n.organization.managePriceTypes.organizationPriceTypesDescription}
              </p>
            </div>

            {isLoading ? (
              <div className="p-8 text-center">
                <Loader2 className="mx-auto size-8 animate-spin" />
                <p className="mt-2 text-sm text-muted-foreground">
                  {i18n.organization.managePriceTypes.loadingMessage}
                </p>
              </div>
            ) : priceTypes.length === 0 ? (
              <div className="p-8 text-center">
                <p className="text-sm text-muted-foreground">
                  {i18n.organization.managePriceTypes.noPriceTypesMessage}
                </p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{i18n.organization.managePriceTypes.codeLabel}</TableHead>
                    <TableHead>{i18n.organization.managePriceTypes.labelLabel}</TableHead>
                    <TableHead className="text-right">
                      {i18n.organization.manageLocales.actionsHeader}
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {priceTypes.map(priceType => (
                    <TableRow key={priceType.id}>
                      <TableCell>
                        <Badge variant="outline">{priceType.code}</Badge>
                      </TableCell>
                      <TableCell>{priceType.label}</TableCell>
                      <TableCell className="text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDelete(priceType)}
                          className="text-destructive hover:text-destructive"
                        >
                          <Trash2 className="size-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            {i18n.organization.managePriceTypes.closeButton}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
